import { useState, useMemo, type ChangeEvent } from 'react';
import * as XLSX from 'xlsx';
import { Trash2, Upload, ArrowLeft, FileSpreadsheet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useAllowlist,
  useImportAllowlist,
  useDeleteAllowlistEntry,
} from '../../lib/queries';
import type { AllowlistEntryInput } from '@w6/shared';

interface ParsedWorkbook {
  fileName: string;
  sheets: { name: string; headers: string[]; rows: Record<string, unknown>[] }[];
}

interface ColumnMapping {
  emailCol: string;
  firstNameCol: string;
  lastNameCol: string;
  fullNameCol: string;
}

const NONE = '__none__';

function readWorkbook(buffer: ArrayBuffer, fileName: string): ParsedWorkbook {
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheets = wb.SheetNames.map((name) => {
    const sheet = wb.Sheets[name];
    if (!sheet) return { name, headers: [], rows: [] };
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
    const headers = rows[0] ? Object.keys(rows[0]) : [];
    return { name, headers, rows };
  });
  return { fileName, sheets };
}

function guessColumn(headers: string[], patterns: RegExp[]): string {
  for (const pattern of patterns) {
    const match = headers.find((h) => pattern.test(h.trim()));
    if (match) return match;
  }
  return NONE;
}

function cellToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function buildEntries(
  rows: Record<string, unknown>[],
  mapping: ColumnMapping,
): { entries: AllowlistEntryInput[]; skipped: number } {
  const entries: AllowlistEntryInput[] = [];
  let skipped = 0;
  for (const row of rows) {
    const email =
      mapping.emailCol !== NONE
        ? cellToString(row[mapping.emailCol]).trim().toLowerCase()
        : '';
    if (!email.includes('@')) {
      skipped++;
      continue;
    }

    let name = '';
    if (mapping.fullNameCol !== NONE) {
      name = cellToString(row[mapping.fullNameCol]).trim();
    } else {
      const first =
        mapping.firstNameCol !== NONE ? cellToString(row[mapping.firstNameCol]).trim() : '';
      const last =
        mapping.lastNameCol !== NONE ? cellToString(row[mapping.lastNameCol]).trim() : '';
      name = `${first} ${last}`.trim();
    }

    if (!name) {
      skipped++;
      continue;
    }

    entries.push({ email, name, role: 'member' });
  }
  return { entries, skipped };
}

export default function ManageAllowlist() {
  const navigate = useNavigate();
  const { data: allowlist, isLoading } = useAllowlist();
  const importMutation = useImportAllowlist();
  const deleteMutation = useDeleteAllowlistEntry();

  const [parsed, setParsed] = useState<ParsedWorkbook | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [selectedSheetName, setSelectedSheetName] = useState<string | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({
    emailCol: NONE,
    firstNameCol: NONE,
    lastNameCol: NONE,
    fullNameCol: NONE,
  });
  const [importResult, setImportResult] = useState<string | null>(null);
  const [importIsError, setImportIsError] = useState(false);

  const selectedSheet = useMemo(
    () => parsed?.sheets.find((s) => s.name === selectedSheetName) ?? null,
    [parsed, selectedSheetName],
  );

  function resetUploadFlow() {
    setParsed(null);
    setSelectedSheetName(null);
    setMapping({ emailCol: NONE, firstNameCol: NONE, lastNameCol: NONE, fullNameCol: NONE });
    setParseError(null);
  }

  function applyDefaultMapping(headers: string[]) {
    setMapping({
      emailCol: guessColumn(headers, [/^e[-\s]?post$/i, /email/i, /mail/i]),
      firstNameCol: guessColumn(headers, [/^förnamn$/i, /^first[\s_]?name$/i, /^fornamn$/i]),
      lastNameCol: guessColumn(headers, [/^efternamn$/i, /^last[\s_]?name$/i, /^surname$/i]),
      fullNameCol: guessColumn(headers, [/^namn$/i, /^name$/i, /full[\s_]?name/i]),
    });
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setParseError(null);
    setImportResult(null);
    try {
      const buffer = await file.arrayBuffer();
      const wb = readWorkbook(buffer, file.name);
      if (wb.sheets.length === 0) {
        setParseError('Inga flikar hittades i filen.');
        return;
      }
      setParsed(wb);
      const first = wb.sheets[0];
      if (first) {
        setSelectedSheetName(first.name);
        applyDefaultMapping(first.headers);
      }
    } catch {
      setParseError('Kunde inte läsa Excel-filen.');
    }
  }

  function handleSheetChange(name: string) {
    setSelectedSheetName(name);
    const sheet = parsed?.sheets.find((s) => s.name === name);
    if (sheet) applyDefaultMapping(sheet.headers);
  }

  function handleImport() {
    if (!selectedSheet) return;
    const hasFullName = mapping.fullNameCol !== NONE;
    const hasFirstOrLast = mapping.firstNameCol !== NONE || mapping.lastNameCol !== NONE;
    if (mapping.emailCol === NONE) {
      setImportResult('Välj en kolumn för e-post.');
      setImportIsError(true);
      return;
    }
    if (!hasFullName && !hasFirstOrLast) {
      setImportResult('Välj antingen "Namn" eller minst en av "Förnamn"/"Efternamn".');
      setImportIsError(true);
      return;
    }
    const { entries, skipped } = buildEntries(selectedSheet.rows, mapping);
    if (entries.length === 0) {
      setImportResult('Inga giltiga rader hittades med vald mappning.');
      setImportIsError(true);
      return;
    }
    void (async () => {
      try {
        const result = await importMutation.mutateAsync(entries);
        const added = result.added;
        const updated = result.updated;
        setImportResult(
          `Klart: ${added} nya, ${updated} uppdaterade${skipped > 0 ? `, ${skipped} hoppades över (saknade email/namn)` : ''}.`,
        );
        setImportIsError(false);
        resetUploadFlow();
      } catch (err) {
        const reason = err instanceof Error ? err.message : 'Okänt fel';
        setImportResult(`Import misslyckades: ${reason}`);
        setImportIsError(true);
      }
    })();
  }

  return (
    <div className="max-w-4xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/admin')}
        className="flex items-center gap-2 text-ink-muted mb-4"
      >
        <ArrowLeft size={16} /> Tillbaka
      </button>

      <h1 className="text-3xl font-serif text-primary mb-6">Allowlist</h1>

      <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
        <h2 className="font-semibold mb-2">Importera från Excel</h2>
        <p className="text-sm text-ink-muted mb-3">
          Stöder .xlsx/.xls med flera flikar och valfri kolumnmappning.
          Du kan välja antingen ett helt &quot;Namn&quot;-fält eller separata
          &quot;Förnamn&quot; + &quot;Efternamn&quot;.
        </p>

        {!parsed ? (
          <label className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-lg cursor-pointer">
            <Upload size={16} />
            <span>Välj fil</span>
            <input type="file" accept=".xlsx,.xls" onChange={handleFile} className="hidden" />
          </label>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <FileSpreadsheet size={16} />
              <span>{parsed.fileName}</span>
              <button
                type="button"
                onClick={resetUploadFlow}
                className="ml-auto text-xs text-red-600 hover:underline"
              >
                Avbryt
              </button>
            </div>

            {parsed.sheets.length > 1 && (
              <label className="block">
                <span className="block text-sm font-medium mb-1">
                  Flik ({parsed.sheets.length} st)
                </span>
                <select
                  value={selectedSheetName ?? ''}
                  onChange={(e) => { handleSheetChange(e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-primary focus:outline-none"
                >
                  {parsed.sheets.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.name} ({s.rows.length} rader)
                    </option>
                  ))}
                </select>
              </label>
            )}

            {selectedSheet && (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <ColumnSelect
                    label="E-post (krävs)"
                    headers={selectedSheet.headers}
                    value={mapping.emailCol}
                    onChange={(v) => { setMapping((m) => ({ ...m, emailCol: v })); }}
                  />
                  <ColumnSelect
                    label="Namn (helnamn-kolumn)"
                    headers={selectedSheet.headers}
                    value={mapping.fullNameCol}
                    onChange={(v) => {
                      setMapping((m) => ({
                        ...m,
                        fullNameCol: v,
                        firstNameCol: v !== NONE ? NONE : m.firstNameCol,
                        lastNameCol: v !== NONE ? NONE : m.lastNameCol,
                      }));
                    }}
                  />
                  <ColumnSelect
                    label="Förnamn"
                    headers={selectedSheet.headers}
                    value={mapping.firstNameCol}
                    disabled={mapping.fullNameCol !== NONE}
                    onChange={(v) => { setMapping((m) => ({ ...m, firstNameCol: v })); }}
                  />
                  <ColumnSelect
                    label="Efternamn"
                    headers={selectedSheet.headers}
                    value={mapping.lastNameCol}
                    disabled={mapping.fullNameCol !== NONE}
                    onChange={(v) => { setMapping((m) => ({ ...m, lastNameCol: v })); }}
                  />
                </div>
                <p className="text-xs text-ink-muted">
                  Tips: använd antingen &quot;Namn&quot; ELLER &quot;Förnamn&quot;+&quot;Efternamn&quot;.
                  Tomma rader hoppas över automatiskt.
                </p>

                {selectedSheet.rows.length > 0 && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-ink-muted">
                      Förhandsgranska 5 rader
                    </summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="text-xs border border-gray-200 w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            {selectedSheet.headers.map((h) => (
                              <th key={h} className="text-left p-2 border-b">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSheet.rows.slice(0, 5).map((r, i) => (
                            <tr key={i} className="border-t">
                              {selectedSheet.headers.map((h) => (
                                <td key={h} className="p-2 truncate max-w-[200px]">
                                  {cellToString(r[h])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}

                <button
                  type="button"
                  onClick={handleImport}
                  disabled={importMutation.isPending}
                  className="bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  <Upload size={16} /> Importera ({selectedSheet.rows.length} rader)
                </button>
              </>
            )}
          </div>
        )}

        {parseError && (
          <p className="mt-3 text-sm text-red-600" role="alert">{parseError}</p>
        )}
        {importResult && (
          <p
            className={`mt-3 text-sm ${importIsError ? 'text-red-600' : 'text-primary'}`}
            role={importIsError ? 'alert' : 'status'}
          >
            {importResult}
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <h2 className="font-semibold p-4 border-b">
          Personer på listan ({String(allowlist?.length ?? 0)})
        </h2>
        {isLoading ? (
          <p className="p-4 text-ink-muted">Laddar…</p>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 text-sm">
              <tr>
                <th className="text-left p-3">Namn</th>
                <th className="text-left p-3">E-post</th>
                <th className="text-left p-3">Roll</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {allowlist?.map((entry) => (
                <tr key={entry.email} className="border-t">
                  <td className="p-3">{entry.name}</td>
                  <td className="p-3">{entry.email}</td>
                  <td className="p-3 capitalize">{entry.role}</td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Ta bort ${entry.email}?`)) deleteMutation.mutate(entry.email);
                      }}
                      className="text-red-600 hover:bg-red-50 p-2 rounded"
                      aria-label={`Ta bort ${entry.email}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface ColumnSelectProps {
  label: string;
  headers: string[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

function ColumnSelect({ label, headers, value, onChange, disabled }: ColumnSelectProps) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => { onChange(e.target.value); }}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-primary focus:outline-none disabled:bg-gray-100 disabled:opacity-60"
      >
        <option value={NONE}>— ingen —</option>
        {headers.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
    </label>
  );
}
